import { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * VoiceMic — records audio, POSTs base64 to /api/ai/ask-voice, and yields
 * both the transcript and the AI answer via onResult({transcript, answer, records_used}).
 */
export default function VoiceMic({ vehicleId, onResult, disabled = false }) {
  const [state, setState] = useState("idle"); // idle | recording | uploading
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Microphone not available");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        setState("uploading");
        try {
          const buf = await blob.arrayBuffer();
          let binary = "";
          const bytes = new Uint8Array(buf);
          for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
          const b64 = btoa(binary);
          const { data } = await api.post("/ai/ask-voice", {
            audio_b64: b64,
            mime_type: "audio/webm",
            vehicle_id: vehicleId || null,
          });
          if (typeof onResult === "function") onResult(data);
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Voice ask failed");
        } finally {
          setState("idle");
        }
      };
      mr.start();
      setState("recording");
    } catch (e) {
      toast.error("Microphone permission denied");
      setState("idle");
    }
  };

  const stop = () => {
    if (mediaRef.current && mediaRef.current.state === "recording") {
      mediaRef.current.stop();
    }
  };

  const busy = state !== "idle";
  const label = state === "recording" ? "Stop" : state === "uploading" ? "Transcribing…" : "Voice";

  return (
    <button
      type="button"
      onClick={state === "recording" ? stop : start}
      disabled={disabled || state === "uploading"}
      data-testid="voice-mic-btn"
      className={
        "inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-colors " +
        (state === "recording"
          ? "bg-red-500/20 border border-red-500 text-red-400"
          : "border border-[var(--border-line)] text-[var(--text-primary)] hover:bg-[var(--hover)]") +
        (disabled ? " opacity-50 cursor-not-allowed" : "")
      }
      title="Ask by voice"
    >
      {state === "recording" ? <Square size={14} /> :
       state === "uploading" ? <Loader2 size={14} className="animate-spin" /> :
       <Mic size={14} />}
      {label}
    </button>
  );
}
